import React, { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import whatsappIcon from "../../assets/nopicture.png";
import MetricsStrip from "../../components/MetricsStrip";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_USERS": {
      const users = action.payload;
      const newUsers = [];

      users.forEach((user) => {
        const userIndex = state.findIndex((item) => item.id === user.id);

        if (userIndex !== -1) {
          state[userIndex] = user;
        } else {
          newUsers.push(user);
        }
      });

      return [...state, ...newUsers];
    }
    case "UPDATE_USERS": {
      const user = action.payload;
      const userIndex = state.findIndex((item) => item.id === user.id);

      if (userIndex !== -1) {
        state[userIndex] = user;
        return [...state];
      }

      return [user, ...state];
    }
    case "DELETE_USER": {
      const userId = action.payload;
      return state.filter((item) => item.id !== userId);
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  userCell: {
    minWidth: 240,
  },
  userContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  userAvatar: {
    width: theme.spacing(5.5),
    height: theme.spacing(5.5),
    flexShrink: 0,
  },
  userMeta: {
    minWidth: 0,
  },
  userName: {
    fontWeight: 600,
  },
  userEmail: {
    opacity: 0.75,
    wordBreak: "break-word",
  },
  statusContent: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  loadMoreBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const Users = () => {
  const classes = useStyles();
  const { user: loggedInUser, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        setLoading(true);

        try {
          if (pageNumber === 1) {
            dispatch({ type: "RESET" });
          }

          const { data } = await api.get("/users/", {
            params: { searchParam, pageNumber },
          });

          dispatch({ type: "LOAD_USERS", payload: data.users || [] });
          setHasMore(Boolean(data.hasMore));
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };

      fetchUsers();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [pageNumber, reloadFlag, searchParam]);

  useEffect(() => {
    if (!loggedInUser) {
      return undefined;
    }

    const companyId = loggedInUser.companyId;

    const onCompanyUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    };

    socket.on(`company-${companyId}-user`, onCompanyUser);

    return () => {
      socket.off(`company-${companyId}-user`, onCompanyUser);
    };
  }, [loggedInUser, socket]);

  const refreshUsers = () => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setReloadFlag((current) => current + 1);
  };

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    dispatch({ type: "RESET" });
    setSearchParam(newSearchParam);
    setPageNumber(1);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    try {
      await api.delete(`/users/${deletingUser.id}`);
      toast.success(i18n.t("users.toasts.deleted"));
      setDeletingUser(null);
      setConfirmModalOpen(false);
      refreshUsers();
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderProfileImage = (user) => {
    const src =
      user.id === loggedInUser.id
        ? loggedInUser.profileImage
          ? `${backendUrl}/public/avatar/${loggedInUser.profileImage}`
          : whatsappIcon
        : user.profileImage
          ? `${backendUrl}/public/avatar/${user.profileImage}`
          : whatsappIcon;

    return <Avatar src={src} alt={user.name} className={classes.userAvatar} />;
  };

  const summary = useMemo(() => {
    const online = users.filter((user) => user.online).length;
    const admins = users.filter((user) => user.profile === "admin").length;

    return {
      total: users.length,
      online,
      offline: users.length - online,
      admins,
      operational: users.length - admins,
    };
  }, [users]);

  const summaryCards = useMemo(
    () => [
      { key: "users", label: "Usuarios", value: summary.total },
      { key: "online", label: "Online", value: summary.online },
      { key: "admins", label: "Administradores", value: summary.admins },
      { key: "team", label: "Equipe", value: summary.operational },
    ],
    [summary]
  );


  if (!loggedInUser) {
    return null;
  }

  if (loggedInUser.profile === "user") {
    return (
      <MainContainer className={classes.mainContainer} fullWidth>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={
          deletingUser
            ? `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
            : i18n.t("users.confirmationModal.deleteTitle")
        }
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingUser(null);
        }}
        onConfirm={handleDeleteUser}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser?.id}
        key={i18n.language}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("users.title")}
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={refreshUsers} disabled={loading}>
              Atualizar
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpenUserModal}>
              {i18n.t("users.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de usuarios" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Usuarios operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar usuarios" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.total} carregados`} />
              <Chip size="small" label={`${summary.online} online`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("users.table.ID")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("users.table.status")}
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    {i18n.t("users.table.name")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("users.table.profile")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    Jornada
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("users.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell align="center">{user.id}</TableCell>
                    <TableCell align="center">
                      <Box className={classes.statusContent}>
                        <UserStatusIcon user={user} />
                        <Typography variant="body2">
                          {user.online ? "Online" : "Offline"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.userCell}>
                      <Box className={classes.userContent}>
                        {renderProfileImage(user)}
                        <Box className={classes.userMeta}>
                          <Typography variant="body2" className={classes.userName}>
                            {user.name}
                          </Typography>
                          <Typography variant="body2" className={classes.userEmail}>
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        variant={user.profile === "admin" ? "default" : "outlined"}
                        color={user.profile === "admin" ? "primary" : "default"}
                        label={user.profile}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {user.startWork || "--"} - {user.endWork || "--"}
                    </TableCell>
                    <TableCell align="center" className={classes.actionsCell}>
                      <IconButton size="small" onClick={() => handleEditUser(user)}>
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingUser(user);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {loading && <TableRowSkeleton columns={6} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && users.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhum usuario encontrado</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre um novo usuario para continuar.
              </Typography>
            </Box>
          )}

          {!loading && hasMore && users.length > 0 && (
            <div className={classes.loadMoreBox}>
              <Button variant="outlined" color="primary" onClick={loadMore}>
                Carregar mais usuarios
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Users;
